import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type ReactNode,
} from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { KbdCombo, HotkeyHintBadge } from '@reflex/ui/components/shared/Kbd';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { useAltKeyHeld } from '@reflex/ui/hooks/useAltKeyHeld';
import { useIsDesktop } from '@reflex/ui/hooks/useIsDesktop';
import { cn } from '@reflex/ui/lib/utils';
import type {
  SegmentedTabItem,
  SegmentedTabsCustomization,
} from '@reflex/ui/components/shared/SegmentedTabs';

interface VerticalTabRailProps {
  value: string;
  onValueChange: (value: string) => void;
  items: SegmentedTabItem[];
  /**
   * Values taken out of the rail. The owner must keep `value` on a visible
   * tab (switch away before hiding the active one); the rail does not keep
   * a hidden selection in sight.
   */
  hidden?: readonly string[];
  /**
   * Same arrange contract as {@link SegmentedTabs}: drag (or Alt+arrow) to
   * reorder visible tabs. When omitted the rail is fixed-order.
   */
  customize?: Pick<SegmentedTabsCustomization, 'onOrderChange'>;
  /**
   * Action / tool chrome stacked under the tabs (pinned actions, overflow
   * menu, sandbox, display, details). Lives in the same column so the
   * header stays title-only.
   */
  bottomContent?: ReactNode;
  'aria-label'?: string;
  className?: string;
  /**
   * Opt-in ⌘/Ctrl+Alt+1..9 switching. Same chord as SegmentedTabs: Alt is
   * required so we never steal the browser's ⌘/Ctrl+digit tab switch. Enable
   * on at most one strip per page.
   */
  numberHotkeys?: boolean;
}

const MAX_HOTKEY_TABS = 9;

/**
 * Move one on-screen tab to another on-screen position and project the result
 * back onto the full item order. Hidden tabs keep their absolute slots.
 */
function reorderIntoSlots(
  order: string[],
  stripValues: string[],
  from: number,
  to: number,
): string[] {
  if (from === to || from < 0 || to < 0 || to >= stripValues.length) return order;
  const moved = arrayMove(stripValues, from, to);
  const slots = order.flatMap((v, i) => (stripValues.includes(v) ? [i] : []));
  const next = [...order];
  slots.forEach((slot, i) => {
    next[slot] = moved[i];
  });
  return next;
}

function useNumberHotkeys(
  enabled: boolean,
  items: SegmentedTabItem[],
  onValueChange: (value: string) => void,
): boolean {
  const hintsVisible = useAltKeyHeld(enabled);
  const itemsRef = useRef(items);
  const onValueChangeRef = useRef(onValueChange);
  useLayoutEffect(() => {
    itemsRef.current = items;
    onValueChangeRef.current = onValueChange;
  });

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const digit =
        /^Digit([1-9])$/.exec(e.code)?.[1] ??
        (/^Numpad[1-9]$/.test(e.code) && /^[1-9]$/.test(e.key) ? e.key : undefined);
      if (!digit || !e.altKey || !(e.metaKey || e.ctrlKey) || e.shiftKey) return;
      const item = itemsRef.current[Number(digit) - 1];
      if (!item) return;
      e.preventDefault();
      onValueChangeRef.current(item.value);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled]);

  return hintsVisible;
}

interface RailTabButtonProps {
  item: SegmentedTabItem;
  selected: boolean;
  hintNumber: number | null;
  hintsVisible: boolean;
  onSelect: () => void;
  onTabKeyDown: (e: ReactKeyboardEvent<HTMLButtonElement>) => void;
  onMoveKeyDown?: (e: ReactKeyboardEvent<HTMLButtonElement>, value: string) => void;
  setNodeRef?: (node: HTMLButtonElement | null) => void;
  style?: CSSProperties;
  listeners?: ReturnType<typeof useSortable>['listeners'];
  isDragging?: boolean;
  sortable?: boolean;
}

function RailTabButton({
  item,
  selected,
  hintNumber,
  hintsVisible,
  onSelect,
  onTabKeyDown,
  onMoveKeyDown,
  setNodeRef,
  style,
  listeners,
  isDragging,
  sortable,
}: RailTabButtonProps) {
  const Icon = item.icon;

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button
          ref={setNodeRef}
          type="button"
          role="tab"
          aria-label={item.label}
          aria-selected={selected}
          data-state={selected ? 'active' : 'inactive'}
          data-tutorial-id={item.dataTutorialId}
          data-slot={sortable ? 'vertical-tab-sortable' : undefined}
          data-hotkey-index={hintNumber ?? undefined}
          tabIndex={selected ? 0 : -1}
          className={cn(
            'group relative flex h-7 w-full items-center justify-center rounded-none shadow-none',
            'text-muted-foreground/60 transition-all duration-150',
            'hover:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'data-[state=active]:bg-muted/40 data-[state=active]:text-foreground',
            'after:hidden data-[state=active]:after:absolute data-[state=active]:after:inset-y-0 data-[state=active]:after:-left-px data-[state=active]:after:w-0.5 data-[state=active]:after:bg-ring data-[state=active]:after:block',
            isDragging && 'z-10 opacity-80',
          )}
          style={style}
          onClick={() => {
            if (!selected) onSelect();
          }}
          onKeyDown={(e) => {
            onMoveKeyDown?.(e, item.value);
            onTabKeyDown(e);
          }}
          {...listeners}
        >
          {Icon ? (
            <Icon
              className="size-4 opacity-70 group-data-[state=active]:opacity-100"
              aria-hidden="true"
            />
          ) : (
            item.label.slice(0, 1)
          )}
          {hintsVisible && hintNumber !== null ? (
            <HotkeyHintBadge keys={['mod', 'alt', String(hintNumber)]} side="left" />
          ) : null}
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" className="flex items-center gap-2">
        <span>{item.label}</span>
        {hintNumber !== null ? (
          <KbdCombo keys={['mod', 'alt', String(hintNumber)]} size="xs" />
        ) : null}
      </TooltipContent>
    </Tooltip>
  );
}

function SortableRailTabButton(
  props: Omit<RailTabButtonProps, 'setNodeRef' | 'style' | 'listeners' | 'isDragging' | 'sortable'>,
) {
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.item.value,
  });
  return (
    <RailTabButton
      {...props}
      sortable
      setNodeRef={setNodeRef}
      isDragging={isDragging}
      listeners={listeners}
      style={{
        // x is zeroed so a tab can only travel along the rail.
        transform: CSS.Translate.toString(transform && { ...transform, x: 0 }),
        transition,
      }}
    />
  );
}

/**
 * Icon-only vertical view switcher for the agent session canvas. Sits on the
 * far right of the workspace as a layout sibling (not an overlay) so
 * Filesystem and Shell keep their full height.
 */
export function VerticalTabRail({
  value,
  onValueChange,
  items,
  hidden = [],
  customize,
  bottomContent,
  'aria-label': ariaLabel,
  className,
  numberHotkeys = false,
}: VerticalTabRailProps) {
  const isDesktop = useIsDesktop();
  const hotkeysEnabled = numberHotkeys && isDesktop;
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );

  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);
  // Visible rail only — hotkey digits track this order, not the full catalog.
  const strip = useMemo(
    () => items.filter((item) => !hiddenSet.has(item.value)),
    [items, hiddenSet],
  );
  const stripValues = useMemo(() => strip.map((item) => item.value), [strip]);
  const hintsVisible = useNumberHotkeys(hotkeysEnabled, strip, onValueChange);
  const sortable = Boolean(customize);

  const applyOrder = (from: number, to: number) => {
    if (!customize) return;
    const order = items.map((item) => item.value);
    const next = reorderIntoSlots(order, stripValues, from, to);
    if (next !== order) customize.onOrderChange(next);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    applyOrder(stripValues.indexOf(String(active.id)), stripValues.indexOf(String(over.id)));
  };

  const handleMoveKeyDown = (e: ReactKeyboardEvent, v: string) => {
    if (!customize || !e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const delta = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
    if (delta === 0) return;
    const from = stripValues.indexOf(v);
    if (from < 0 || from + delta < 0 || from + delta >= stripValues.length) return;
    e.preventDefault();
    applyOrder(from, from + delta);
  };

  const onTabKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    if (e.altKey) return;
    if (strip.length < 2) return;
    e.preventDefault();
    const idx = strip.findIndex((item) => item.value === value);
    const delta = e.key === 'ArrowDown' ? 1 : -1;
    const next = strip[(idx + delta + strip.length) % strip.length];
    if (next) onValueChange(next.value);
  };

  if (strip.length === 0 && !bottomContent) return null;

  const tabs = strip.map((item, index) => {
    const selected = item.value === value;
    // Digit = on-screen ordinal (1-based), never the full catalog index.
    const n = index < MAX_HOTKEY_TABS ? index + 1 : null;
    const shared = {
      item,
      selected,
      hintNumber: n,
      hintsVisible,
      onSelect: () => onValueChange(item.value),
      onTabKeyDown,
    };
    return sortable ? (
      <SortableRailTabButton key={item.value} {...shared} onMoveKeyDown={handleMoveKeyDown} />
    ) : (
      <RailTabButton key={item.value} {...shared} />
    );
  });

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="vertical"
      data-testid="agent-vertical-tab-rail"
      className={cn(
        'flex w-9 shrink-0 flex-col items-stretch gap-0.5 border-l border-border/30 bg-panel/40 py-1.5',
        className,
      )}
    >
      {sortable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={stripValues} strategy={verticalListSortingStrategy}>
            {tabs}
          </SortableContext>
        </DndContext>
      ) : (
        tabs
      )}
      {bottomContent ? (
        <div
          className="mt-auto flex w-full flex-col items-center gap-0.5 pt-1"
          data-testid="agent-rail-bottom"
        >
          {bottomContent}
        </div>
      ) : null}
    </div>
  );
}
